import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// ─── All logic untouched ────────────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      setError('Username dan password wajib diisi');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const user = await login(form.username, form.password);
      toast.success(`Ahlan, ${user.name}!`);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Username atau password salah');
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared form card (used on both mobile & desktop) ──────────────────
  const FormCard = (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="w-full"
    >
      {/* Back button */}
      <button
        data-testid="login-back-btn"
        onClick={() => navigate('/welcome')}
        className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 text-sm font-medium mb-7 transition-colors"
      >
        <ArrowLeft size={15} />
        Kembali
      </button>

      {/* Heading */}
      <div className="mb-7">
        <h2 className="font-playfair font-bold text-2xl text-emerald-950 mb-1">Masuk</h2>
        <p className="text-gray-500 text-sm">Gunakan username &amp; password dari admin</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Username */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Username / NIS
          </label>
          <input
            data-testid="login-username-input"
            type="text"
            value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            placeholder="Masukkan username atau NIS"
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 text-sm
              focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 focus:bg-white
              transition-all duration-200"
            autoComplete="username"
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              data-testid="login-password-input"
              type={showPass ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Masukkan password"
              className="w-full px-4 py-3 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 text-sm
                focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 focus:bg-white
                transition-all duration-200"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPass(s => !s)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="login-error"
            className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-2.5 rounded-xl"
          >
            {error}
          </motion.div>
        )}

        {/* Submit */}
        <button
          data-testid="login-submit-btn"
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800
            disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all duration-200
            shadow-sm hover:shadow-md mt-1 text-sm"
        >
          {loading
            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : 'Masuk'}
        </button>
      </form>

      {/* Footer hint */}
      <p className="text-xs text-gray-400 text-center mt-6">
        Belum punya akun? Hubungi admin pesantren
      </p>
    </motion.div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div data-testid="login-page" className="min-h-screen bg-white flex">

      {/* ══ LEFT PANEL — desktop/tablet only ══ */}
      <div className="hidden md:flex flex-col items-center justify-center w-[46%] lg:w-[52%] relative overflow-hidden bg-emerald-950">
        {/* Subtle radial glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(16,185,129,0.18) 0%, transparent 70%)' }} />

        {/* Decorative circles */}
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-emerald-900/50 pointer-events-none" />
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-800/40 pointer-events-none" />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 flex flex-col items-center text-center px-10"
        >
          <img src="/logo-zahbaq.png" alt="ZahBaq"
            className="w-20 h-20 object-contain mb-5 drop-shadow-lg" />
          <h1 className="font-playfair font-bold text-white text-3xl lg:text-4xl tracking-tight mb-3">
            ZahBaq
          </h1>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-emerald-400 rounded-full" />
            <span className="text-emerald-300 text-xs font-medium tracking-wider lowercase">
              sistem manajemen tahfizh
            </span>
            <div className="h-px w-8 bg-emerald-400 rounded-full" />
          </div>
          <p className="text-emerald-300/80 text-sm leading-relaxed max-w-xs">
            Platform digital pengelolaan hafalan, absensi, dan perkembangan santri pesantren.
          </p>
        </motion.div>
      </div>

      {/* ══ RIGHT PANEL — form ══ */}
      <div className="flex-1 flex flex-col relative overflow-hidden bg-white">

        {/* Mobile-only decorations */}
        <div className="md:hidden absolute pointer-events-none"
          style={{ top: -50, left: -60, width: 220, height: 240,
            background: 'linear-gradient(135deg, #a7f3d0 0%, #d1fae5 60%, #ecfdf5 100%)',
            borderRadius: '38% 62% 60% 40% / 35% 30% 70% 65%', opacity: 0.55 }} />
        <div className="md:hidden absolute pointer-events-none"
          style={{ top: -35, right: -45, width: 160, height: 180,
            background: 'linear-gradient(225deg, #6ee7b7 0%, #a7f3d0 55%, #d1fae5 100%)',
            borderRadius: '60% 40% 35% 65% / 50% 60% 40% 50%', opacity: 0.35 }} />

        {/* Mobile-only logo */}
        <div className="md:hidden flex flex-col items-center pt-16 pb-6 relative z-10">
          <img src="/logo-zahbaq.png" alt="ZahBaq"
            className="w-16 h-16 object-contain mb-2" />
          <h1 className="font-playfair font-bold text-emerald-950 text-xl tracking-tight">ZahBaq</h1>
          <div className="flex items-center gap-2.5 mt-1">
            <div className="h-px w-6 bg-emerald-400 rounded-full" />
            <span className="text-[10px] text-emerald-600 font-medium tracking-wider lowercase">
              sistem manajemen tahfizh
            </span>
            <div className="h-px w-6 bg-emerald-400 rounded-full" />
          </div>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-6 py-8 relative z-10">
          <div className="w-full max-w-[420px] md:bg-white md:shadow-md md:border md:border-gray-100 md:rounded-2xl md:p-8">
            {FormCard}
          </div>
        </div>

        {/* Desktop bottom brand */}
        <p className="hidden md:block text-center text-xs text-gray-300 pb-5">
          ZahBaq · Pesantren Baitul Quran
        </p>
      </div>
    </div>
  );
}
